import React, { useRef, useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  Divider,
  CircularProgress,
  Button,
  Paper,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  PictureAsPdf as PdfIcon,
  Business as BusinessIcon
} from '@mui/icons-material'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { payrollApi } from '../../../api/payrollApi'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { useParams, useNavigate } from 'react-router-dom'

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2
  }).format(amount || 0)
}

const getMonthName = (monthNumber) => {
  const date = new Date()
  date.setMonth(monthNumber - 1)
  return date.toLocaleString('default', { month: 'long' })
}

const PayslipPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  
  const [payslipData, setPayslipData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const payslipRef = useRef(null)

  useEffect(() => {
    if (id) {
      fetchPayslip()
    }
  }, [id])

  const fetchPayslip = async () => {
    setLoading(true)
    try {
      const response = await payrollApi.getPayslip({ payroll_id: id })
      setPayslipData(response.data)
    } catch (error) {
      console.error('Error fetching payslip:', error)
      toast.error('Failed to load payslip details')
      navigate('/org/payroll')
    } finally {
      setLoading(false)
    }
  }

  const exportPDF = async () => {
    if (!payslipRef.current || !payslipData) return
    
    setExporting(true)
    try {
      const element = payslipRef.current
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false
      })
      
      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      })
      
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight)
      
      const fileName = `Payslip_${payslipData.employee?.employee_code}_${getMonthName(payslipData.payroll?.month)}_${payslipData.payroll?.year}.pdf`
      pdf.save(fileName)
      toast.success('Payslip downloaded successfully')
    } catch (error) {
      console.error('Error generating PDF:', error)
      toast.error('Failed to generate PDF')
    } finally {
      setExporting(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'background.default' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!payslipData || !payslipData.employee || !payslipData.payroll) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'background.default' }}>
        <Typography color="error" variant="h6" mb={2}>Failed to load payslip data.</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/org/payroll')} variant="contained">
          Back to Payroll
        </Button>
      </Box>
    )
  }

  const { employee, payroll } = payslipData

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4, px: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: '1000px', mx: 'auto', mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/org/payroll')}
          sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
        >
          Back to Payroll
        </Button>
        <Button 
          variant="contained" 
          startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <PdfIcon />} 
          onClick={exportPDF} 
          disabled={exporting}
          sx={{ boxShadow: 2, borderRadius: 2 }}
        >
          {exporting ? 'Generating...' : 'Download PDF'}
        </Button>
      </Box>

      <Paper 
        ref={payslipRef} 
        sx={{ 
          maxWidth: '1000px', 
          mx: 'auto', 
          bgcolor: 'background.paper',
          boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.08)',
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <BusinessIcon 
          sx={{ 
            position: 'absolute', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)', 
            fontSize: 450, 
            color: 'rgba(25, 118, 210, 0.02)', 
            pointerEvents: 'none',
            zIndex: 0
          }} 
        />

        <Box sx={{ p: { xs: 4, md: 6 }, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar sx={{ bgcolor: '#1976d2', width: 72, height: 72, borderRadius: 2 }}>
                <BusinessIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight="800" color="#1565c0" sx={{ letterSpacing: '-0.5px' }}>
                  Intellisia Technologies
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  123 Business Tech Park, Sector 62, Noida, UP 201309
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  contact@intellisiatechnologies.com | +91 123 456 7890
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h3" sx={{ color: '#e0e0e0', fontWeight: '900', letterSpacing: '2px', textTransform: 'uppercase' }}>
                PAYSLIP
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" color="text.secondary" sx={{ mt: 1 }}>
                For {getMonthName(payroll.month)} {payroll.year}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3, mb: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Box mb={1.5}><Typography variant="caption" color="text.secondary" display="block">Employee Name</Typography><Typography variant="body2" fontWeight="700">{employee.first_name} {employee.last_name}</Typography></Box>
                <Box mb={1.5}><Typography variant="caption" color="text.secondary" display="block">Employee ID</Typography><Typography variant="body2" fontWeight="600">{employee.employee_code}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary" display="block">Designation</Typography><Typography variant="body2" fontWeight="600">{employee.designation || '-'}</Typography></Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box mb={1.5}><Typography variant="caption" color="text.secondary" display="block">Department</Typography><Typography variant="body2" fontWeight="600">{employee.department || '-'}</Typography></Box>
                <Box mb={1.5}><Typography variant="caption" color="text.secondary" display="block">Date of Joining</Typography><Typography variant="body2" fontWeight="600">{employee.join_date ? format(new Date(employee.join_date), 'dd MMM yyyy') : '-'}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary" display="block">Pay Period</Typography><Typography variant="body2" fontWeight="600">01 {getMonthName(payroll.month)} - {new Date(payroll.year, payroll.month, 0).getDate()} {getMonthName(payroll.month)} {payroll.year}</Typography></Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box mb={1.5}><Typography variant="caption" color="text.secondary" display="block">UAN No.</Typography><Typography variant="body2" fontWeight="600">100XXXXXX456</Typography></Box>
                <Box mb={1.5}><Typography variant="caption" color="text.secondary" display="block">PF No.</Typography><Typography variant="body2" fontWeight="600">UP/NOI/XXXXX/123</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary" display="block">Bank Account</Typography><Typography variant="body2" fontWeight="600">XXXX-XXXX-3456 (HDFC)</Typography></Box>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #eee', pb: 2, mb: 4 }}>
            <Box textAlign="center" flex={1}>
              <Typography variant="h6" fontWeight="bold">{payroll.total_working_days}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Working Days</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box textAlign="center" flex={1}>
              <Typography variant="h6" fontWeight="bold" color="success.main">{payroll.days_present}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Present</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box textAlign="center" flex={1}>
              <Typography variant="h6" fontWeight="bold" color="error.main">{payroll.days_absent}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Absent</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box textAlign="center" flex={1}>
              <Typography variant="h6" fontWeight="bold" color="warning.main">{payroll.lop_days}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>LOP Days</Typography>
            </Box>
          </Box>

          {/* Unified Ledger Table */}
          <TableContainer component={Box} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', mb: 5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#1976d2' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5, width: '30%' }}>EARNINGS</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', py: 1.5, width: '20%', borderRight: '1px solid rgba(255,255,255,0.2)' }}>AMOUNT</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', py: 1.5, width: '30%', pl: 3 }}>DEDUCTIONS</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', py: 1.5, width: '20%' }}>AMOUNT</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ '& td': { py: 1.5, borderBottom: '1px solid #f1f5f9' } }}>
                <TableRow>
                  <TableCell>Basic Salary</TableCell>
                  <TableCell align="right">{formatCurrency(payroll.base_salary)}</TableCell>
                  <TableCell sx={{ borderLeft: '1px solid #e2e8f0', pl: 3 }}>Loss of Pay (LOP)</TableCell>
                  <TableCell align="right" sx={{ color: payroll.lop_deduction > 0 ? 'error.main' : 'inherit' }}>
                    {payroll.lop_deduction > 0 ? formatCurrency(payroll.lop_deduction) : '-'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Overtime Pay ({payroll.overtime_hours || 0} hrs)</TableCell>
                  <TableCell align="right">{payroll.overtime_pay > 0 ? formatCurrency(payroll.overtime_pay) : '-'}</TableCell>
                  <TableCell sx={{ borderLeft: '1px solid #e2e8f0', pl: 3 }}>Provident Fund (PF)</TableCell>
                  <TableCell align="right">-</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>House Rent Allowance</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell sx={{ borderLeft: '1px solid #e2e8f0', pl: 3 }}>Professional Tax</TableCell>
                  <TableCell align="right">-</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Special Allowance</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell sx={{ borderLeft: '1px solid #e2e8f0', pl: 3 }}>Income Tax (TDS)</TableCell>
                  <TableCell align="right">-</TableCell>
                </TableRow>
                
                {/* Subtotals */}
                <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc', '& td': { fontWeight: 'bold', py: 2, borderBottom: 'none' } }}>
                  <TableCell>Total Earnings</TableCell>
                  <TableCell align="right" sx={{ color: 'primary.dark' }}>{formatCurrency(payroll.base_salary + payroll.overtime_pay)}</TableCell>
                  <TableCell sx={{ borderLeft: '1px solid #e2e8f0', pl: 3 }}>Total Deductions</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main' }}>{formatCurrency(payroll.lop_deduction)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ 
            bgcolor: '#1565c0', 
            color: 'white',
            p: 3, 
            borderRadius: 2, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 6,
            boxShadow: '0 8px 24px rgba(21, 101, 192, 0.3)'
          }}>
            <Box>
              <Typography variant="subtitle1" sx={{ opacity: 0.9, letterSpacing: 1 }}>NET PAYABLE SALARY</Typography>
              <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>Amount transferred to employee's bank account</Typography>
            </Box>
            <Typography variant="h3" fontWeight="800" sx={{ letterSpacing: '-1px' }}>{formatCurrency(payroll.net_salary)}</Typography>
          </Box>
          <Box sx={{ borderTop: '1px solid #eee', pt: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              <strong>Disclaimer:</strong> This is a computer generated document and does not require a physical signature. The amounts mentioned herein are subject to statutory deductions as per applicable laws.
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block">
              Intellisia Technologies | Confidential Document | Generated on {format(new Date(payroll.generated_at), 'dd MMM yyyy HH:mm')}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default PayslipPage
